import React, { useState } from 'react';

import { Dispatcher } from '$partials/listings';
import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base/ajax-listing.jsx';
import { ArrowRightIcon } from '$partials/icon';
import { Button, Filter } from '$partials/ui';
import { Stream } from '$partials/modules';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').ArticleListingPage}
 */
const ArticleListingPage = (props) => {

    const page = props.data.page;
    const [ selectedCategory, setSelectedCategory ] = useState(page.items?.filters?.category);

    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        page: page.items?.pageInfo?.page || 1,
        path: `/api/v1/listings/${encodeURIComponent(props.data.uri)}`,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
        query: {
            'category' : selectedCategory === 'all' ? null : selectedCategory
        }
    });

    const chunkArray = (arr, chunkCount) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += chunkCount){
            chunks.push(arr.slice(i, i + chunkCount));
        }
        return chunks;
    };

    return (
        <main className='container'>
            <div
                className='flex-grow py6 md-py7 md-pr0 bg-white flex items-center pl5 md-pl6 border-bottom border-top grey'>
                <h2 className='type-style-2 black'>
                    {page.title}
                </h2>
            </div>
            <div
                className='flex-grow py6 md-py7 md-pr0 bg-white flex flex-wrap items-center pl5 md-pl6 border-bottom border-top grey'>
                <Filter
                    className='mr-2px mb-2px'
                    onClick={() => {
                        if (selectedCategory === 'all') {
                            setSelectedCategory('');
                        } else {
                            setSelectedCategory('all');
                        }
                    }}
                    selected={selectedCategory === 'all'}
                    singleSelect={true}
                    title={'All'}/>

                {page.categoryFilterOptions?.map(category => {
                    return <Filter
                        className='mr-2px mb-2px'
                        key={category.id}
                        onClick={() => {
                            if (selectedCategory === category.value) {
                                setSelectedCategory('');
                            } else {
                                setSelectedCategory(category.value);
                            }
                        }}
                        selected={selectedCategory === category.value}
                        singleSelect={true}
                        title={category.label}/>;
                })}

            </div>

            {page.headerModules?.length > 0 && (
                <Stream
                    classes='module--y-bleed border-top border-bottom grey'
                    namespace='article-listing-page'
                    page={page} />
            )}

            <AjaxListingContext.Provider value={listing}>
                {listing.items?.length > 0 ? (
                    <>
                        <div className='hide md-show'>
                            <div className='flex flex-wrap col-12'>
                                {chunkArray(listing.items, 4)?.map((items, idx) => {
                                    if(idx % 2 === 0) {
                                        const fixedLeft = items[0];
                                        const rightCol = items.slice?.(1);
                                        return (
                                            <div
                                                className='col-12 flex'
                                                key={fixedLeft?.node?.id}>
                                                <div className='col-7 border-right border-top border-bottom grey'>
                                                    {fixedLeft?.node && (
                                                        <Dispatcher
                                                            _isLargeCol={true}
                                                            item={fixedLeft.node}/>
                                                    )}
                                                </div>
                                                <div className='col-5 flex flex-col'>
                                                    {rightCol?.length > 0 && rightCol.map((item) => {
                                                        return(
                                                            <div
                                                                className={'border-top border-bottom grey border-left'}
                                                                key={item?.node?.id}>
                                                                <Dispatcher item={item?.node}/>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        const leftCol = items.slice?.(0,3);
                                        const fixedRight = items[3];
                                        return (
                                            <div
                                                className='col-12 flex'
                                                key={fixedRight?.node?.id}>
                                                <div className='col-5 flex flex-col'>
                                                    {leftCol?.length > 0 && leftCol.map((item) => {
                                                        return(
                                                            <div
                                                                className={'border-top border-bottom grey border-right'}
                                                                key={item?.node?.id}>
                                                                <Dispatcher item={item?.node}/>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <div className='col-7 border-left border-top border-bottom grey'>
                                                    {fixedRight?.node && (
                                                        <Dispatcher
                                                            _isLargeCol={true}
                                                            item={fixedRight.node}/>
                                                    )}
                                                </div>
                                            </div>);
                                    }
                                })}
                            </div>
                        </div>
                        <div className='md-hide'>
                            {listing.items?.map((item)=> {
                                return (
                                    <div
                                        className='col-12 border-top border-bottom grey'
                                        key={item?.node?.id}>
                                        <Dispatcher item={item?.node}/>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <div className='col-12 border-top border-bottom bg-white grey py9 px5 md-px6'>
                        <p className='type-style-5 mb3 black'>Please choose different filters</p>
                        <h2 className='type-style-2 black'>No Articles Found</h2>
                        <div className='col-12 md-col-4'>
                            <Button
                                as = 'button'
                                className = 'bg-none type-style-5'
                                iconClassName='bg-black white button border-radius-100px p3 md-p5'
                                look = 'underline'
                                onClick={() => setSelectedCategory('')}
                                renderIcon = {<ArrowRightIcon/>}>
                                    Reset Filters
                            </Button>
                        </div>

                    </div>
                )}

                {listing?.page < listing?.totalPages && (
                    <InfinitePagination>
                        <div>
                            <p className='type-style-5 px5 py7'>Loading</p>
                        </div>
                    </InfinitePagination>
                )}
            </AjaxListingContext.Provider>
        </main>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ArticleListingPage;
